import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { Divider, Form } from 'antd';
import type { CheckboxProps as AntdCheckboxProps } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classNames from 'classnames';
import { Modal } from 'shared/ui/Modal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useGetAllFranchisesQuery } from 'entities/Franchise';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { CheckboxGroup } from 'shared/ui/CheckboxGroup';
import { showNotification } from 'app/providers/NotificationsProvider';

import { getExportModalState } from '../model/selectors/getExportModalState';
import { exportModalActions } from '../model/slice/exportModalSlice';
import { ColumnOption, ExportEntity } from '../model/types';
import { INVOICE_FRANCHISE_COLUMNS } from '../model/constants/invoiceFranchiseColumns';
import { downloadExcelFile } from '../utils/helpers/downloadExcelFile';

interface ExportFormValues {
  columns?: string[];
}

const getExportGridColumns = (columnsCount: number): 2 | 3 | 4 => {
  if (columnsCount > 30) {
    return 4;
  }

  if (columnsCount > 16) {
    return 3;
  }

  return 2;
};

const EXPORT_MODAL_WIDTH: Record<2 | 3 | 4, number> = {
  2: 600,
  3: 850,
  4: 1100,
};

const EXPORT_GRID_COLS_CLASS: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

export const ExportModal: FC = memo(() => {
  const { isOpened, filters, columns, entity } = useAppSelector(getExportModalState);

  const [isLoading, setLoading] = useState(false);
  const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<string[]>([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>([]);

  const dispatch = useAppDispatch();
  const [form] = Form.useForm<ExportFormValues>();
  const { t } = useAppTranslation('common');

  const isInvoiceExport = entity === ExportEntity.INVOICE;

  const { data: franchises = [], isLoading: isFranchisesLoading } = useGetAllFranchisesQuery(undefined, {
    skip: !isOpened || !isInvoiceExport,
  });

  const columnOptions: ColumnOption[] = useMemo(() => {
    return columns?.map((column) => ({ label: column.title, value: column.key })) || [];
  }, [columns]);

  const allColumnKeys = useMemo(() => columnOptions.map(({ value }) => value), [columnOptions]);

  const franchiseOptions = useMemo(() => {
    return franchises.map(({ franchiseId, name }) => ({ label: name, value: franchiseId }));
  }, [franchises]);

  const exportGridColumns = useMemo(() => getExportGridColumns(columnOptions.length), [columnOptions.length]);
  const exportGridColsClass = EXPORT_GRID_COLS_CLASS[exportGridColumns];
  const modalWidth = EXPORT_MODAL_WIDTH[exportGridColumns];

  const checkAll = allColumnKeys.length > 0 && selectedColumnKeys.length === allColumnKeys.length;
  const indeterminate = selectedColumnKeys.length > 0 && selectedColumnKeys.length < allColumnKeys.length;

  const getColumnKeysByFranchiseIds = useCallback(
    (franchiseIds: string[]): string[] => {
      const keys = franchiseIds.flatMap((franchiseId) => {
        const franchise = franchises.find((item) => item.franchiseId === franchiseId);

        return franchise ? INVOICE_FRANCHISE_COLUMNS[franchise.name] || [] : [];
      });

      return Array.from(new Set(keys));
    },
    [franchises],
  );

  const mapSelectedColumns = useCallback(
    (selectedKeys: string[]) => {
      return columnOptions
        .filter((option) => selectedKeys.includes(option.value))
        .map((column) => ({ title: column.label, key: column.value }));
    },
    [columnOptions],
  );

  const closeModal = useCallback((): void => {
    setSelectedFranchiseIds([]);
    setSelectedColumnKeys([]);
    form.resetFields();
    dispatch(exportModalActions.closeModal());
  }, [dispatch, form]);

  const handleSelectAllChange: AntdCheckboxProps['onChange'] = useCallback(
    (e: CheckboxChangeEvent) => {
      setSelectedColumnKeys(e.target.checked ? allColumnKeys : []);
    },
    [allColumnKeys],
  );

  const handleFranchiseChange = useCallback(
    (checkedValue: CheckboxValueType[]) => {
      const franchiseIds = checkedValue.map(String);

      setSelectedFranchiseIds(franchiseIds);
      setSelectedColumnKeys(getColumnKeysByFranchiseIds(franchiseIds));
    },
    [getColumnKeysByFranchiseIds],
  );

  const handleColumnsChange = useCallback((checkedValue: CheckboxValueType[]) => {
    setSelectedColumnKeys(checkedValue.map(String));
  }, []);

  const handleDownloadExcelFile = useCallback(
    async (values: ExportFormValues) => {
      try {
        setLoading(true);

        const selectedKeys = isInvoiceExport ? selectedColumnKeys : values.columns || [];
        const fields = mapSelectedColumns(selectedKeys);

        if (!fields.length) {
          showNotification('error', t('Error'), t('Please select at least 1 column!'));

          return;
        }

        await downloadExcelFile(entity, filters, fields);

        showNotification('info', t('Success'), t('Downloaded!'));
        closeModal();
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error'), t('Error when downloading data'));
      } finally {
        setLoading(false);
      }
    },
    [closeModal, entity, filters, isInvoiceExport, mapSelectedColumns, selectedColumnKeys, t],
  );

  return (
    <Modal width={modalWidth} isOpen={isOpened} onClose={closeModal}>
      <Form form={form} layout="vertical" onFinish={handleDownloadExcelFile} className="flex flex-col gap-4 py-0">
        <div className="font-semibold text-3xl">{t('Export data')}</div>
        <div>{t('Select the columns you want to export to Excel')}</div>

        {isInvoiceExport ? (
          isFranchisesLoading ? (
            <div>{t('Loading...')}</div>
          ) : (
            <>
              <div className={classNames('grid gap-4 items-start', exportGridColsClass)}>
                <Checkbox name="select-all" indeterminate={indeterminate} onChangeEvent={handleSelectAllChange} checked={checkAll}>
                  <span>{t('Select all')}</span>
                </Checkbox>
                <CheckboxGroup
                  name="franchises"
                  options={franchiseOptions}
                  showSelectAll={false}
                  className="flex flex-col gap-2"
                  value={selectedFranchiseIds}
                  onChange={handleFranchiseChange}
                />
              </div>
              <Divider className="my-0" />
              <CheckboxGroup
                name="columns"
                options={columnOptions}
                showSelectAll={false}
                className={classNames(
                  'grid gap-x-4 gap-y-3 items-start [&>.ant-checkbox-wrapper]:!mr-0 [&>.ant-checkbox-wrapper]:items-start [&>.ant-checkbox-wrapper_.ant-checkbox]:top-0.5',
                  exportGridColsClass,
                )}
                value={selectedColumnKeys}
                onChange={handleColumnsChange}
              />
            </>
          )
        ) : (
          <Form.Item name="columns" rules={[{ required: true, message: t('Please select at least 1 column!') }]}>
            <CheckboxGroup name="columns" options={columnOptions} className={classNames('grid gap-4', exportGridColsClass)} />
          </Form.Item>
        )}

        <div className="flex space-x-3 justify-end">
          <Button theme="secondary" onClick={closeModal}>
            {t('Cancel')}
          </Button>
          <Button type="submit" isLoading={isLoading || isFranchisesLoading}>
            {t('Download')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
});
